import React, { useEffect, useState } from 'react'
import styles from './CheckoutFormIndirizzoBlock.module.sass'
import { Button, FormikField, FormLayout } from '@components/atoms'
import useUtente from '@hooks/useUtente'
import { Plus } from '@components/icons'
import CheckoutFormPartIndirizzo from '../CheckoutFormPartIndirizzo/CheckoutFormPartIndirizzo'
import useTrans from '@hooks/useTrans'
import {
  useCreateAddressMutation,
  useValidaIndirizzoMutation,
  useValidaCodicePostaleMutation,
} from '@gql/graphql'
import { parseBackendValidationErrors } from '@utils/formUtils'

interface Props {
  title?: string
  variant?: 'spedizione' | 'fatturazione'
  formikProps?: any
  addAddressOpen?: boolean
  setAddAddressOpen?: (v: boolean) => void
  className?: string
}

const CheckoutFormIndirizzoBlock = (props: Props) => {
  const {
    title = undefined,
    variant = 'spedizione',
    formikProps = undefined,
    addAddressOpen = false,
    setAddAddressOpen = undefined,
    className = '',
  } = props

  const t = useTrans()
  const { utente, refetchUtente } = useUtente()
  const indirizzi =
    variant === 'fatturazione' ? utente?.indirizziFatturazione : utente?.indirizziSpedizione

  const [createAddress] = useCreateAddressMutation()
  const [validaIndirizzo] = useValidaIndirizzoMutation()
  const [validaCodicePostale] = useValidaCodicePostaleMutation()

  const firstLetterUppercase = (value) => {
    return value.charAt(0).toUpperCase() + value.slice(1)
  }

  return (
    <div className={`${styles.root} ${className}`}>
      {title && <h3 className={styles.title}>{title}</h3>}
      <FormLayout>
        {!addAddressOpen && !!indirizzi?.length ? (
          <>
            <FormikField
              formik={formikProps}
              id={`checkout_indirizzo_${variant}_id`}
              name={
                variant === 'fatturazione' ? 'indirizzoFatturazioneId' : 'indirizzoSpedizioneId'
              }
              type="radio"
              options={indirizzi?.map((indirizzo) => ({
                value: `${indirizzo?.pk}`,
                label: `${indirizzo?.nome} ${indirizzo.cognome}`,
                description: `<p>${indirizzo?.indirizzo}, ${indirizzo.citta} ${indirizzo.cap}</p><p>${indirizzo.telefono}</p>`,
              }))}
              layout="full"
            />
          </>
        ) : (
          <>
            <CheckoutFormPartIndirizzo formikProps={formikProps} variant={variant} />
            {!!indirizzi?.length && (
              <>
                <Button
                  label={t('Salva nuovo indirizzo')}
                  size="lg"
                  onClick={async () => {
                    if (!formikProps.isValid) return

                    const cap = formikProps.values[`cap${firstLetterUppercase(variant)}`]
                    const cittaProvincia =
                      formikProps.values[`citta${firstLetterUppercase(variant)}`]
                    const [citta, provincia] = cittaProvincia.split('|')
                    const indirizzo =
                      formikProps.values[`indirizzo${firstLetterUppercase(variant)}`]
                    const countryCode =
                      formikProps.values[`stato${firstLetterUppercase(variant)}`] || 'IT'

                    // Validazione CAP
                    try {
                      const { data: capData } = await validaCodicePostale({
                        variables: {
                          postalCode: cap,
                          countryCode: countryCode,
                          stateCode: provincia,
                          carrierCode: 'FDXE',
                        },
                      })

                      if (!capData?.validaCodicePostale?.valid) {
                        formikProps.setFieldError(
                          `cap${firstLetterUppercase(variant)}`,
                          t('Il codice postale non è valido')
                        )
                        return
                      }
                    } catch (error) {
                      formikProps.setFieldError(
                        `cap${firstLetterUppercase(variant)}`,
                        t('Errore durante la validazione del CAP')
                      )
                      return
                    }

                    // Validazione indirizzo completo
                    try {
                      const { data: addressData } = await validaIndirizzo({
                        variables: {
                          address: {
                            street: indirizzo,
                            city: citta,
                            postalCode: cap,
                            countryCode: countryCode,
                          },
                        },
                      })

                      if (!addressData?.validaIndirizzo?.valid) {
                        formikProps.setFieldError(
                          `indirizzo${firstLetterUppercase(variant)}`,
                          t("L'indirizzo non è valido o non è stato trovato")
                        )
                        return
                      }
                    } catch (error) {
                      formikProps.setFieldError(
                        `indirizzo${firstLetterUppercase(variant)}`,
                        t("Errore durante la validazione dell'indirizzo")
                      )
                      return
                    }

                    // Se tutte le validazioni passano, crea l'indirizzo
                    const { data } = await createAddress({
                      variables: {
                        address: {
                          nome: formikProps.values[`nome${firstLetterUppercase(variant)}`],
                          cognome: formikProps.values[`cognome${firstLetterUppercase(variant)}`],
                          indirizzo: indirizzo,
                          note: formikProps.values[`info${firstLetterUppercase(variant)}`],
                          citta: citta,
                          provincia: provincia,
                          stato: countryCode,
                          cap: cap,
                          telefono: formikProps.values[`telefono${firstLetterUppercase(variant)}`],
                          isFatturazione: variant === 'fatturazione',
                        },
                      },
                    })

                    if (!data?.createAddress?.status) {
                      const errors = parseBackendValidationErrors(
                        data?.createAddress?.message || ''
                      )
                      if (errors) {
                        Object.entries(errors).forEach(([key, value]) =>
                          formikProps.setFieldError(`${key}${firstLetterUppercase(variant)}`, value)
                        )
                      }
                      return
                    }

                    const newAddressId = data?.createAddress?.pk
                    formikProps.setFieldValue(
                      variant === 'fatturazione'
                        ? 'indirizzoFatturazioneId'
                        : 'indirizzoSpedizioneId',
                      newAddressId.toString()
                    )
                    await refetchUtente()
                    setAddAddressOpen(false)
                  }}
                />
              </>
            )}
          </>
        )}
        {!addAddressOpen && (
          <>
            {variant !== 'fatturazione' && (
              <FormikField
                formik={formikProps}
                id={'checkout_stesso_indirizzo'}
                name="stessoIndirizzo"
                type="checkbox"
                checkboxLabel={t('Usa questo indirizzo come indirizzo di fatturazione')}
                layout="full"
              />
            )}
            {!!indirizzi?.length && (
              <div className={styles.addBox}>
                <Button
                  label={t('Aggiungi nuovo indirizzo')}
                  variant="ghost"
                  size="sm"
                  iconLeft={<Plus />}
                  onClick={() => setAddAddressOpen(true)}
                />
              </div>
            )}
          </>
        )}
      </FormLayout>
    </div>
  )
}

export default CheckoutFormIndirizzoBlock
